import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Users, LogIn, Clock, UserX, Coffee, TrendingUp, AlarmClock, Timer, LogOut,
} from "lucide-react";
import { ResponsiveContainer, Tooltip, CartesianGrid, XAxis, YAxis, Bar, BarChart } from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function StatCard({ label, value, icon: Icon, tone = "default", suffix, testid, onClick }) {
  const toneMap = {
    default: "text-foreground",
    success: "text-emerald-500",
    warning: "text-amber-500",
    danger: "text-red-500",
    primary: "text-blue-500",
  };
  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      onClick={onClick}
      data-testid={testid}
      className="glass-card rounded-xl p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-150 text-left w-full disabled:opacity-60 disabled:cursor-not-allowed"
      disabled={!onClick}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
        <div className={`w-9 h-9 rounded-lg bg-secondary flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-display text-3xl font-bold tracking-tight">
        {value ?? "—"}
        {suffix && <span className="text-base ml-1 text-muted-foreground font-medium">{suffix}</span>}
      </div>
    </motion.button>
  );
}

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const time = now.toLocaleTimeString("tr-TR");
  const date = now.toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return (
    <div className="glass-card rounded-xl p-5" data-testid="live-clock">
      <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
        <Timer className="w-3 h-3" /> Canlı Saat
      </div>
      <div className="font-mono text-4xl font-bold tracking-tight tabular-nums">{time}</div>
      <div className="text-sm text-muted-foreground mt-1 capitalize">{date}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // { type, label, items, loading }
  // Manual-checkout modal state
  const [manualCo, setManualCo] = useState(null); // { attendanceId, employeeName, checkInIso }
  const [manualTime, setManualTime] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [manualBusy, setManualBusy] = useState(false);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/dashboard/stats");
      setStats(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    const id = setInterval(loadStats, 20000);
    return () => clearInterval(id);
  }, []);

  const openDetail = async (type, label) => {
    setDetail({ type, label, items: [], loading: true });
    try {
      const { data } = await api.get("/dashboard/details", { params: { type } });
      setDetail({ type, label, items: data, loading: false });
    } catch {
      setDetail({ type, label, items: [], loading: false });
    }
  };

  const startManualCheckout = (item) => {
    const e = item.employee || {};
    const r = item.record || {};
    // Pre-fill the picker with current Turkey local time in "YYYY-MM-DDTHH:MM"
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const hh = String(now.getHours()).padStart(2, "0");
    const mi = String(now.getMinutes()).padStart(2, "0");
    setManualTime(`${yyyy}-${mm}-${dd}T${hh}:${mi}`);
    setManualNote("");
    setManualCo({
      attendanceId: r.id,
      employeeName: `${e.first_name || ""} ${e.last_name || ""}`.trim(),
      checkInIso: r.check_in,
    });
  };

  const submitManualCheckout = async () => {
    if (!manualCo) return;
    setManualBusy(true);
    try {
      await api.patch(`/attendance/${manualCo.attendanceId}/manual-checkout`, {
        check_out_time: manualTime,
        note: manualNote,
      });
      toast.success(`${manualCo.employeeName} için çıkış kaydedildi`);
      setManualCo(null);
      // Refresh both stats and current dialog list
      loadStats();
      if (detail) openDetail(detail.type, detail.label);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setManualBusy(false); }
  };

  const hours = Math.floor((stats?.total_worked_minutes || 0) / 60);
  const mins = (stats?.total_worked_minutes || 0) % 60;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Personel devam durumu ve canlı istatistikler</p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-32 rounded-xl" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <StatCard testid="stat-checkedin" onClick={() => openDetail("checkedin", "Bugün Giriş Yapan Personel")} label="Bugün Giriş Yapan" value={stats.checked_in_today} icon={LogIn} tone="primary" />
            <StatCard testid="stat-inside" onClick={() => openDetail("inside", "Şu Anda İçeride Olanlar")} label="Şu Anda İçeride" value={stats.inside_now} icon={Users} tone="success" />
            <StatCard testid="stat-late" onClick={() => openDetail("late", "Geç Kalan Personeller")} label="Geç Kalan" value={stats.late_today} icon={AlarmClock} tone="warning" />
            <StatCard testid="stat-absent" onClick={() => openDetail("absent", "Devamsız Personeller")} label="Devamsız" value={stats.absent_today} icon={UserX} tone="danger" />
            <StatCard testid="stat-onleave" onClick={() => openDetail("onleave", "İzinli Personeller")} label="İzinli" value={stats.on_leave} icon={Coffee} tone="primary" />
            <StatCard testid="stat-overtime" onClick={() => openDetail("overtime", "Fazla Mesai Yapanlar")} label="Fazla Mesai" value={stats.overtime_count} icon={Clock} tone="warning" />
            <StatCard testid="stat-worked" label="Toplam Çalışma" value={`${hours}sa ${mins}dk`} icon={Timer} tone="default" />
            <StatCard testid="stat-rate" label="Aylık Devam" value={stats.monthly_attendance_rate} suffix="%" icon={TrendingUp} tone="success" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="glass-card rounded-xl p-5 lg:col-span-2" data-testid="weekly-chart">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-display font-semibold text-lg tracking-tight">Haftalık Çalışma</div>
                  <div className="text-xs text-muted-foreground">Son 7 gün toplam çalışılan saat</div>
                </div>
              </div>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={stats.weekly} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(150,150,150,0.15)" />
                    <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} stroke="currentColor" opacity={0.6} />
                    <YAxis fontSize={11} stroke="currentColor" opacity={0.6} />
                    <Tooltip
                      contentStyle={{ background: "rgba(20,20,26,0.9)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }}
                      cursor={{ fill: "rgba(37,99,235,0.06)" }}
                    />
                    <Bar dataKey="hours" fill="#2563EB" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <LiveClock />
          </div>

          <div className="glass-card rounded-xl p-5" data-testid="recent-activity">
            <div className="font-display font-semibold text-lg tracking-tight mb-4">Son Hareketler</div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {(stats.recent_activity || []).length === 0 && (
                <div className="text-sm text-muted-foreground py-8 text-center">Henüz hareket kaydı yok.</div>
              )}
              {(stats.recent_activity || []).map((l) => (
                <div key={l.id} className="flex items-center justify-between text-sm py-2 border-b border-border/40 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className={`inline-block w-2 h-2 rounded-full ${l.action === "checkin" ? "bg-emerald-500" : "bg-blue-500"}`} />
                    <span className="font-medium">{l.action === "checkin" ? "Giriş" : "Çıkış"}</span>
                    <span className="text-muted-foreground font-mono text-xs">{l.employee_id?.slice(0, 8)}</span>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">
                    {new Date(l.timestamp).toLocaleString("tr-TR")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="stat-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.label}</DialogTitle>
          </DialogHeader>
          {detail?.loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Yükleniyor…</div>
          ) : (detail?.items || []).length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Kayıt yok.</div>
          ) : (
            <div className="space-y-2" data-testid="stat-detail-list">
              {detail.items.map((it, i) => {
                const e = it.employee || {};
                const r = it.record || {};
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 border border-border/40">
                    {e.photo_url ? (
                      <img src={e.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center font-display font-bold text-sm">
                        {e.first_name?.[0]}{e.last_name?.[0]}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{e.first_name} {e.last_name}</div>
                      <div className="text-xs text-muted-foreground font-mono">#{e.employee_no} · {e.department || "-"}</div>
                    </div>
                    <div className="text-right text-xs font-mono">
                      {detail.type === "late" && (
                        <span className="text-amber-500">+{r.late_minutes} dk</span>
                      )}
                      {detail.type === "overtime" && (
                        <span className="text-blue-500">+{r.overtime_minutes} dk</span>
                      )}
                      {(detail.type === "checkedin" || detail.type === "inside") && r.check_in && (
                        <div>{new Date(r.check_in).toLocaleTimeString("tr-TR", {hour: "2-digit", minute: "2-digit"})}</div>
                      )}
                      {detail.type === "inside" && (
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 ml-1" />
                      )}
                      {detail.type === "onleave" && r.type && (
                        <div className="capitalize">{r.type}</div>
                      )}
                    </div>
                    {detail.type === "inside" && r.id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(ev) => { ev.stopPropagation(); startManualCheckout(it); }}
                        className="gap-1.5 h-8 border-blue-500/50 text-blue-500 hover:bg-blue-500/10 hover:text-blue-500 ml-2"
                        data-testid={`manual-checkout-btn-${e.id || i}`}
                      >
                        <LogOut className="w-3.5 h-3.5" /> Çıkış Yap
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!manualCo} onOpenChange={(o) => !o && setManualCo(null)}>
        <DialogContent data-testid="manual-checkout-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-500">
              <LogOut className="w-5 h-5" /> Manuel Çıkış
            </DialogTitle>
          </DialogHeader>
          {manualCo && (
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-muted-foreground text-xs">Personel</div>
                <div className="font-display font-semibold text-lg tracking-tight">
                  {manualCo.employeeName}
                </div>
                {manualCo.checkInIso && (
                  <div className="text-xs text-muted-foreground font-mono mt-1">
                    Giriş: {new Date(manualCo.checkInIso).toLocaleString("tr-TR")}
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Çıkış Saati</Label>
                <Input
                  type="datetime-local"
                  value={manualTime}
                  onChange={(e) => setManualTime(e.target.value)}
                  data-testid="manual-checkout-time"
                />
                <div className="text-xs text-muted-foreground">
                  Türkiye saati · giriş saatinden sonraki bir zaman olmalı
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Not (opsiyonel)</Label>
                <Input
                  value={manualNote}
                  onChange={(e) => setManualNote(e.target.value)}
                  placeholder="Örn. Çıkış okutmayı unuttu · Ali Bey onayladı"
                  data-testid="manual-checkout-note"
                />
              </div>

              <div className="flex gap-2 pt-1">
                <Button variant="outline" onClick={() => setManualCo(null)} className="flex-1">
                  İptal
                </Button>
                <Button
                  onClick={submitManualCheckout}
                  disabled={manualBusy || !manualTime}
                  className="flex-1 bg-blue-600 hover:bg-blue-500"
                  data-testid="manual-checkout-save"
                >
                  {manualBusy ? "Kaydediliyor..." : "Çıkış Yap"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
